import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import { FileText } from 'lucide-react';
import { Button, Modal, StatusPill, DocumentViewer } from '../../components';
import { agencyService } from '../../services/agencyService';
import {
  CARD, inputClass, FieldLabel, Segmented, errorMessage, localToday, addDays, plural,
} from '../carehome_manager/care_planner/shared';

/**
 * What the agency screens share — the agency's own workspace, the care home's
 * Agencies page and the admin's Agencies page all describe the same bookings,
 * so the words and colours for each state live here, once.
 *
 * Everything clock-dependent (upcoming, due, in progress, missed, hours to
 * approve) arrives from the API (api/utils/agency.js bookingState); these only
 * turn it into words.
 */

export { CARD, inputClass, FieldLabel, Segmented, errorMessage, localToday, addDays, plural };

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '');

/** A stored /api/files/... or /uploads/... path as a link the browser can open. */
export const fileUrl = (path) => (!path ? '#' : /^https?:/.test(path) ? path : `${API_BASE}${path}`);

/* ------------------------------------------------------------- dates - */

const at = (ymd) => new Date(`${String(ymd).slice(0, 10)}T12:00:00`);
export const dayLabel = (ymd) => (ymd ? at(ymd).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) : '—');
export const longDay = (ymd) => (ymd ? at(ymd).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : '—');
export const timeRange = (b) => `${b.start_time}–${b.end_time}${b.end_date && b.end_date !== b.shift_date ? ' (+1)' : ''}`;

const LONDON = 'Europe/London';
/** 'HH:MM' London time of an ISO instant. */
export const clockOf = (iso) => (iso
  ? new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: LONDON })
  : '—');
/** 'YYYY-MM-DDTHH:MM' London time — for <input type="datetime-local">. */
export const localInputOf = (iso) => {
  if (!iso) return '';
  const p = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
    timeZone: LONDON, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date(iso)).filter((x) => x.type !== 'literal').map((x) => [x.type, x.value]));
  return `${p.year}-${p.month}-${p.day}T${p.hour === '24' ? '00' : p.hour}:${p.minute}`;
};
export const stampOf = (iso) => (iso
  ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: LONDON })
  : '—');

export const hoursOf = (minutes) => {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
};
export const money = (n) => (n == null ? '—' : `£${Number(n).toFixed(2)}`);

export const WEEKDAYS = [
  { value: 1, short: 'Mon' }, { value: 2, short: 'Tue' }, { value: 3, short: 'Wed' }, { value: 4, short: 'Thu' },
  { value: 5, short: 'Fri' }, { value: 6, short: 'Sat' }, { value: 7, short: 'Sun' },
];
export const daysLabel = (days) => (Array.isArray(days) && days.length
  ? (days.length === 7 ? 'Every day' : WEEKDAYS.filter((d) => days.includes(d.value)).map((d) => d.short).join(', '))
  : 'Days not reported');

/* ------------------------------------------------------------ states - */

/** A booking's state (bookingState) → label + pill tone. */
export const BOOKING_STATE = {
  offered: { label: 'Offered', tone: 'info' },
  // Confirmed = brand blue, Completed = green, Did not attend = red — the
  // client's colours, on both the agency's and the care home's screens.
  upcoming: { label: 'Confirmed', tone: 'info' },
  due: { label: 'Not clocked in', tone: 'warn' },
  in_progress: { label: 'On shift', tone: 'info' },
  completed: { label: 'Completed', tone: 'good' },
  missed: { label: 'Missed — no clocking', tone: 'crit' },
  no_show: { label: 'Did not attend', tone: 'crit' },
  rejected: { label: 'Turned down', tone: 'mute' },
  withdrawn: { label: 'Withdrawn', tone: 'mute' },
  cancelled: { label: 'Cancelled', tone: 'mute' },
};
/**
 * Can the AGENCY cancel this booking? Confirmed, nobody clocked in, shift not
 * over (upcoming / due) — the same line the API draws (agency cancelBooking).
 */
export const agencyCanCancel = (b) => b?.status === 'confirmed' && ['upcoming', 'due'].includes(b.state);

export const BookingPill = ({ booking }) => {
  const meta = BOOKING_STATE[booking.state] || { label: booking.state, tone: 'mute' };
  return <StatusPill tone={meta.tone}>{meta.label}</StatusPill>;
};

export const HOURS_STATE = {
  to_approve: { label: 'To approve', tone: 'warn' },
  approved: { label: 'Approved', tone: 'good' },
  none: { label: '—', tone: 'mute' },
};
export const HoursPill = ({ booking }) => {
  if (booking.state === 'missed') return <StatusPill tone="crit">No clocking</StatusPill>;
  if (booking.state === 'no_show') return <StatusPill tone="mute">No hours</StatusPill>;
  if (booking.state === 'in_progress') return <StatusPill tone="info">On shift</StatusPill>;
  const meta = HOURS_STATE[booking.hours] || HOURS_STATE.none;
  return <StatusPill tone={meta.tone}>{meta.label}</StatusPill>;
};

export const REQUEST_STATUS = {
  needs_cover: { label: 'Needs cover', tone: 'warn' },
  offers: { label: 'Offers to review', tone: 'info' },
  covered: { label: 'Covered', tone: 'good' },
  unfilled: { label: 'Not filled', tone: 'crit' },
  cancelled: { label: 'Cancelled', tone: 'mute' },
};

export const CONNECTION_STATUS = {
  pending: { label: 'Pending', tone: 'warn' },
  connected: { label: 'Connected', tone: 'good' },
  declined: { label: 'Declined', tone: 'crit' },
  cancelled: { label: 'Cancelled', tone: 'mute' },
  archived: { label: 'Archived', tone: 'mute' },
};

export const APPROVAL_STATE = {
  approved: { label: 'Approved', tone: 'good' },
  review: { label: 'Needs review', tone: 'warn' },
  changes: { label: 'Changes requested', tone: 'crit' },
};

/* ---------------------------------------------------------------- UI - */

/** Pill filter tabs with counts — Connected (3) · Pending (1) · History (5). */
export const FilterTabs = ({ tabs, value, onChange }) => (
  <div className="flex flex-wrap gap-2">
    {tabs.map((t) => {
      const active = t.value === value;
      return (
        <button
          key={t.value}
          type="button"
          onClick={() => onChange(t.value)}
          className={`inline-flex items-center gap-2 h-9 px-3.5 rounded-full text-[13px] font-semibold border transition-colors ${active
            ? 'bg-blue-600 border-blue-600 text-white'
            : 'bg-white dark:bg-[#262b33] border-[#DCE4EF] dark:border-gray-700 text-[#475569] dark:text-gray-300 hover:border-blue-300'}`}
        >
          {t.label}
          {t.count != null && (
            <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold grid place-items-center ${active
              ? 'bg-white/20 text-white'
              : t.alert ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300' : 'bg-[#EEF2F6] dark:bg-gray-800 text-[#64748B] dark:text-gray-400'}`}
            >
              {t.count}
            </span>
          )}
        </button>
      );
    })}
  </div>
);

/** A centred empty state inside a card. */
export const EmptyCard = ({ icon: Icon, title, text, action }) => (
  <div className={`${CARD} px-6 py-14 text-center`}>
    {Icon && (
      <div className="mx-auto mb-3 w-12 h-12 rounded-2xl bg-[#EEF3FF] dark:bg-blue-500/10 text-blue-600 dark:text-blue-300 grid place-items-center">
        <Icon className="w-6 h-6" />
      </div>
    )}
    <p className="text-[15px] font-bold text-[#0B1220] dark:text-gray-100">{title}</p>
    {text && <p className="mt-1 text-[13px] text-[#64748B] dark:text-gray-400 max-w-md mx-auto">{text}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

/** Label / value pair for detail panels. */
export const Field = ({ label, children }) => (
  <div className="min-w-0">
    <p className="text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8] dark:text-gray-500">{label}</p>
    <div className="mt-0.5 text-[13.5px] text-[#0B1220] dark:text-gray-100 break-words">{children ?? '—'}</div>
  </div>
);

/** Proof files as links that open in a new tab. */
/**
 * A worker's DBS / right to work / training files. Opening one shows it in the
 * page's DocumentViewer — never a new tab — on the agency's Staff drawer and
 * the care home's Agency staff review alike.
 */
export const DocList = ({ docs = [], onRemove }) => {
  const [preview, setPreview] = useState(null);
  if (!docs.length) return <p className="text-[13px] text-[#94A3B8]">No file uploaded</p>;
  return (
    <>
      <ul className="flex flex-col gap-1.5 min-w-0">
        {docs.map((d) => (
          <li key={d.path} className="flex items-center gap-2 text-[13px] min-w-0">
            <FileText className="w-4 h-4 text-[#94A3B8] shrink-0" />
            <button
              type="button"
              onClick={() => setPreview({
                url: fileUrl(d.path),
                name: d.name,
                kind: String(d.mime || '').startsWith('image/') ? 'image' : undefined,
              })}
              className="text-left text-blue-600 dark:text-blue-400 hover:underline truncate"
            >
              {d.name}
            </button>
            {onRemove && (
              <button type="button" onClick={() => onRemove(d)} className="ml-auto text-[12px] font-semibold text-rose-600 hover:underline shrink-0">Remove</button>
            )}
          </li>
        ))}
      </ul>
      <DocumentViewer file={preview} onClose={() => setPreview(null)} />
    </>
  );
};

/** The agency cancelling a confirmed booking — reason required, the home sees it. */
export const CancelBookingModal = ({ booking, onClose, onDone }) => (
  <ReasonModal
    open={Boolean(booking)}
    title={`Cancel ${booking?.worker?.name || ''}'s booking`}
    intro={booking ? `${booking.home?.name || 'The care home'} · ${dayLabel(booking.shift_date)} · ${timeRange(booking)}. The care home is told straight away and the place opens again; ${booking.worker?.name || 'the carer'} is told in the app.` : ''}
    label="Reason for the care home"
    placeholder="e.g. Carer is unwell"
    confirmLabel="Cancel booking"
    onClose={onClose}
    onSubmit={async (reason) => {
      const res = await agencyService.cancelBooking(booking.id, reason);
      toast.success(res.message);
      onDone();
    }}
  />
);

/**
 * Ask for a reason (or optional note) before doing something — decline,
 * cancel, archive, reject. `onSubmit(text)` returns a promise; the modal stays
 * open with the error if it rejects.
 */
export const ReasonModal = ({
  open, title, intro, label = 'Reason', placeholder, required = true, confirmLabel = 'Confirm',
  variant = 'danger', onClose, onSubmit,
}) => {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { if (open) { setText(''); setError(''); } }, [open]);

  const submit = async () => {
    if (required && !text.trim()) { setError(`${label} is required.`); return; }
    setBusy(true);
    setError('');
    try {
      await onSubmit(text.trim());
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen={open}
      onClose={busy ? undefined : onClose}
      title={title}
      footer={(
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose} disabled={busy}>Back</Button>
          <Button variant={variant} onClick={submit} loading={busy}>{confirmLabel}</Button>
        </div>
      )}
    >
      {intro && <p className="text-[13.5px] text-[#475569] dark:text-gray-300 mb-3">{intro}</p>}
      <FieldLabel required={required}>{label}</FieldLabel>
      <textarea
        className={`${inputClass} h-24 py-2 resize-none`}
        value={text}
        placeholder={placeholder}
        onChange={(e) => setText(e.target.value)}
        maxLength={500}
      />
      {error && <p className="mt-2 text-[12.5px] text-rose-600">{error}</p>}
    </Modal>
  );
};

/**
 * What was recorded during a worked shift — COUNTS only. The records
 * themselves are the care home's (api/controllers/agency/insights.controller.js).
 */
export const ACTIVITY_LABELS = [
  ['care_notes', 'care notes'],
  ['care_tasks', 'care tasks'],
  ['vitals', 'vitals'],
  ['weights', 'weights'],
  ['medication', 'medication doses'],
  ['incidents', 'incidents'],
];
export const activityTotal = (a) => (a ? ACTIVITY_LABELS.reduce((s, [k]) => s + (a[k] || 0), 0) : 0);
export const ActivityChips = ({ activity, empty = 'Nothing recorded' }) => {
  const parts = activity ? ACTIVITY_LABELS.filter(([k]) => activity[k] > 0) : [];
  if (!parts.length) return <span className="text-[12px] text-[#94A3B8]">{activity ? empty : '—'}</span>;
  return (
    <span className="flex flex-wrap gap-1.5">
      {parts.map(([k, label]) => (
        <span key={k} className="text-[11.5px] font-semibold px-2 py-0.5 rounded-full bg-[#EEF3FF] text-blue-700 dark:bg-blue-500/15 dark:text-blue-300">
          {activity[k]} {label}
        </span>
      ))}
    </span>
  );
};

/** "Late 12 min · left 20 min early" — empty when on time. */
export const punctuality = (att) => {
  if (!att) return '';
  const bits = [];
  if (att.late_minutes) bits.push(`late ${att.late_minutes} min`);
  if (att.left_early_minutes) bits.push(`left ${att.left_early_minutes} min early`);
  return bits.join(' · ');
};

/** A row of facts about one shift: date, time, home/agency, unit. */
export const ShiftLine = ({ item, place }) => (
  <div className="min-w-0">
    <p className="text-[14px] font-bold text-[#0B1220] dark:text-gray-100">
      {dayLabel(item.shift_date)} · {timeRange(item)}
    </p>
    <p className="text-[12.5px] text-[#64748B] dark:text-gray-400 truncate">
      {[place, item.job_role, item.unit].filter(Boolean).join(' · ')}
    </p>
  </div>
);
